import { Add, ContentCopy, Delete, Edit } from "@mui/icons-material"
import QrCodeIcon from "@mui/icons-material/QrCode"
import { Box, Card, CardContent, IconButton, Tooltip, Typography } from "@mui/material"
import { GridColDef, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { QRCodeGenerator } from "components/QRCodeGenerator/QRCodeGenerator"
import { Device, DeviceType, Project } from "graphql/types"
import { useState } from "react"
import { getDeviceTypeIcon } from "utils/devices"

type ProjectDeviceListProps = {
  project: Project
  onEdit: (id: string) => void
  onAdd: (arg: DeviceType) => void
  onDelete: (id: string[]) => void
  onCopy: (id: string) => void
}

export const ProjectDeviceList = (props: ProjectDeviceListProps) => {
  const { project, onEdit, onAdd, onDelete, onCopy } = props
  const [deleteDevice, setDeleteDevice] = useState<string[] | null>(null)
  const [openQRCodeGenerator, setOpenQRCodeGenerator] = useState(false)
  const [qrCodeNumber, setQRCodeNumber] = useState<{
    barcode: string
    name: string
  }>({ barcode: "", name: "" })

  const handleDeviceDelete = () => {
    if (deleteDevice) {
      onDelete(deleteDevice)
      setDeleteDevice(null)
    }
  }

  const handleQRCode = (row: Device) => {
    setOpenQRCodeGenerator(true)
    setQRCodeNumber({ barcode: row.barcode, name: row.name })
  }

  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: "Typ",
      flex: 25,
      renderCell: (params) => getDeviceTypeIcon(params.row.type),
    },
    { field: "name", headerName: "Name", flex: 100 },
    { field: "barcode", headerName: "Barcode", flex: 100 },
    {
      field: "devices",
      headerName: "Anzahl EUT in Group",
      flex: 100,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>
            {params.row.type === DeviceType.EUT_GROUP ? params.row.devices.length : "-"}
          </Typography>
        </Box>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      flex: 100,
      renderCell: (params) => (
        <>
          <Tooltip title="Bearbeiten">
            <IconButton onClick={() => onEdit(params.row.id)} size="small">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Löschen">
            <IconButton onClick={() => setDeleteDevice([params.row.id])} size="small">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kopieren">
            <IconButton onClick={() => onCopy(params.row.id)} size="small">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="QR-Code für das Geräte erzeugen">
            <IconButton onClick={() => handleQRCode(params.row)} size="small">
              <QrCodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ]

  return (
    <Card>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 177px)",
          overflow: "auto",
        }}
      >
        {project && project.devices && (
          <DataGridPro
            getRowId={(row) => row.id}
            rows={project.devices.filter(
              (device) => device.type === DeviceType.EUT_GROUP || device.type === DeviceType.AUX
            )}
            columns={columns}
            disableMultipleRowSelection={true}
            slots={{
              toolbar: () => (
                <GridToolbarContainer
                  sx={{
                    margin: 1,
                  }}
                >
                  <StyledButton
                    buttonType="custom"
                    customIcon={<Add />}
                    onClick={() => onAdd(DeviceType.EUT_GROUP)}
                    variant="contained"
                  >
                    EUT hinzufügen
                  </StyledButton>

                  <StyledButton
                    buttonType="custom"
                    customIcon={<Add />}
                    onClick={() => onAdd(DeviceType.AUX)}
                    variant="contained"
                  >
                    AUX hinzufügen
                  </StyledButton>

                  <GridToolbarQuickFilter />
                </GridToolbarContainer>
              ),
            }}
            localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          />
        )}
      </CardContent>

      <DeleteDialog
        open={deleteDevice !== null}
        onClose={() => setDeleteDevice(null)}
        onDelete={handleDeviceDelete}
      />

      <QRCodeGenerator
        open={openQRCodeGenerator}
        onClose={() => setOpenQRCodeGenerator(false)}
        barcode={qrCodeNumber.barcode}
        title={qrCodeNumber.name}
      />
    </Card>
  )
}
